/**
 * SlashCommandMenu — the chat input's slash-command palette.
 *
 * A self-contained command palette rendered above the textarea: a search box at
 * the top, then a two-level collapsible tree — top-level "Skills" vs "Tools",
 * each with sub-groups (e.g. "Incident Management", "Objectives", "Data
 * Sources"). Both top-level sections default open, but their sub-groups default
 * collapsed so the palette opens compact. Searching auto-expands everything.
 * Keyboard navigation runs over the currently visible command rows (headers are
 * skipped). Picking a command calls `onSelect`, which prefills its editable
 * prompt template into the input — see useAICommands.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import type { AICommand } from './hooks/useAICommands';

interface SlashCommandMenuProps {
  commands: AICommand[];
  onSelect: (command: AICommand) => void;
  onClose: () => void;
}

const KIND_LABEL: Record<AICommand['kind'], string> = {
  skill: 'Skills',
  tool: 'Tools',
};

const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({
  commands,
  onSelect,
  onClose,
}) => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [activeIndex, setActiveIndex] = useState(0);

  const searchRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  const query = search.trim().toLowerCase();
  const searching = query.length > 0;

  const filtered = useMemo(() => {
    if (!searching) {
      return commands;
    }
    return commands.filter(
      (c) =>
        c.command.toLowerCase().includes(query) ||
        c.label.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query)
    );
  }, [commands, searching, query]);

  // Two-level grouping, preserving the pre-sorted (kind, then group, then label) order:
  // top-level kind ("Skills" / "Tools") → sub-group ("Incident Management", "Objectives", …).
  const tree = useMemo(() => {
    const byKind = new Map<AICommand['kind'], Map<string, AICommand[]>>();
    for (const cmd of filtered) {
      let subGroups = byKind.get(cmd.kind);
      if (!subGroups) {
        subGroups = new Map<string, AICommand[]>();
        byKind.set(cmd.kind, subGroups);
      }
      const list = subGroups.get(cmd.group);
      if (list) {
        list.push(cmd);
      } else {
        subGroups.set(cmd.group, [cmd]);
      }
    }
    return Array.from(byKind.entries()).map(
      ([kind, subGroups]) => [kind, Array.from(subGroups.entries())] as const
    );
  }, [filtered]);

  // Sub-group collapse key (top-level kind uses its own key).
  const subKey = (kind: AICommand['kind'], group: string) =>
    `${kind}::${group}`;

  // Both top-level sections default open so their headers are visible, but
  // sub-groups default collapsed so the palette opens compact and searchable.
  // Searching opens everything. An explicit toggle (boolean in `expanded`)
  // overrides the default either way.
  const isKindOpen = (kind: AICommand['kind']) =>
    searching || (expanded[kind] ?? true);
  const isSubOpen = (kind: AICommand['kind'], group: string) =>
    searching || (expanded[subKey(kind, group)] ?? false);

  // Flattened list of visible rows (in open kind + open sub-group) — the keyboard nav target.
  const visible = useMemo(() => {
    const rows: AICommand[] = [];
    for (const [kind, subGroups] of tree) {
      if (!isKindOpen(kind)) continue;
      for (const [group, cmds] of subGroups) {
        if (isSubOpen(kind, group)) {
          rows.push(...cmds);
        }
      }
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, searching, expanded]);

  // Toggle a collapse key. Seed with the current effective open-state so the first
  // click always flips what the user sees (the default may be open via isKind/SubOpen).
  const toggle = (key: string, currentlyOpen: boolean) =>
    setExpanded((prev) => ({ ...prev, [key]: !currentlyOpen }));

  const safeIndex = visible.length
    ? Math.min(activeIndex, visible.length - 1)
    : 0;
  const activeCommand = visible[safeIndex];

  // Focus the search box when the palette opens.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [safeIndex]);

  // Close when clicking outside the palette (the external "/" trigger button
  // toggles itself, so don't treat a click on it as an outside click).
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (containerRef.current?.contains(target)) {
        return;
      }
      if (target.closest('#dgpt-command-trigger')) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(visible.length ? (safeIndex + 1) % visible.length : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(
        visible.length ? (safeIndex - 1 + visible.length) % visible.length : 0
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeCommand) {
        onSelect(activeCommand);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="ChatDisasterGPT-slash-menu"
      ref={containerRef}
      role="dialog"
    >
      <div className="ChatDisasterGPT-slash-search">
        <SharedIcon iconName="search" size="sm" />
        <input
          ref={searchRef}
          type="text"
          className="form-control flex-fill border-0 bg-transparent p-0"
          placeholder="Search commands"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div className="ChatDisasterGPT-slash-list" role="listbox">
        {/* Only "not found" when nothing matches — collapsed-but-present sections still render their headers. */}
        {filtered.length === 0 && (
          <div className="ChatDisasterGPT-slash-empty">No commands found.</div>
        )}
        {tree.map(([kind, subGroups]) => {
          const kindOpen = isKindOpen(kind);
          const kindCount = subGroups.reduce(
            (sum, [, cmds]) => sum + cmds.length,
            0
          );
          return (
            <div key={kind} className="ChatDisasterGPT-slash-kind">
              <button
                type="button"
                className="ChatDisasterGPT-slash-top-header"
                aria-expanded={kindOpen}
                // preventDefault on mousedown keeps focus in the search box.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggle(kind, kindOpen)}
              >
                <SharedIcon
                  iconName={kindOpen ? 'expand_more' : 'chevron_right'}
                  size="sm"
                />
                <span className="ChatDisasterGPT-slash-group-name">
                  {KIND_LABEL[kind]}
                </span>
                <span className="ChatDisasterGPT-slash-group-count">
                  {kindCount}
                </span>
              </button>
              {kindOpen &&
                subGroups.map(([group, cmds]) => {
                  const open = isSubOpen(kind, group);
                  return (
                    <div key={group} className="ChatDisasterGPT-slash-group">
                      <button
                        type="button"
                        className="ChatDisasterGPT-slash-group-header"
                        aria-expanded={open}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => toggle(subKey(kind, group), open)}
                      >
                        <SharedIcon
                          iconName={open ? 'expand_more' : 'chevron_right'}
                          size="sm"
                        />
                        <span className="ChatDisasterGPT-slash-group-name">
                          {group}
                        </span>
                        <span className="ChatDisasterGPT-slash-group-count">
                          {cmds.length}
                        </span>
                      </button>
                      {open &&
                        cmds.map((cmd) => {
                          const active = activeCommand?.command === cmd.command;
                          return (
                            <button
                              key={cmd.command}
                              ref={active ? activeRef : undefined}
                              type="button"
                              role="option"
                              aria-selected={active}
                              className={
                                'ChatDisasterGPT-slash-item' +
                                (active ? ' is-active' : '')
                              }
                              title={
                                [cmd.imhReference, cmd.entryConditions]
                                  .filter(Boolean)
                                  .join(' — ') || undefined
                              }
                              onMouseDown={(e) => {
                                e.preventDefault();
                                onSelect(cmd);
                              }}
                              onMouseEnter={() =>
                                setActiveIndex(visible.indexOf(cmd))
                              }
                            >
                              <span className="ChatDisasterGPT-slash-token">
                                {cmd.command}
                              </span>
                              <span className="ChatDisasterGPT-slash-label">
                                {cmd.label}
                              </span>
                              <span className="ChatDisasterGPT-slash-desc">
                                {cmd.description}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SlashCommandMenu;
